import type { PersonalityType } from './types';

export const PERSONALITY_TYPES: PersonalityType[] = [
  'ENTJ', 'ENTP', 'ENFJ', 'ENFP',
  'ESTJ', 'ESTP', 'ESFJ', 'ESFP',
  'INTJ', 'INTP', 'INFJ', 'INFP',
  'ISTJ', 'ISTP', 'ISFJ', 'ISFP',
];

// Dashboard grid layout: 4x4 matching the mockup design
export const DASHBOARD_TYPE_GRID: PersonalityType[][] = [
  ['INTJ', 'INFJ', 'ENTJ', 'ENTP'],
  ['INTP', 'INFP', 'ENFJ', 'ENFP'],
  ['ISTJ', 'ISFJ', 'ESTJ', 'ESFP'],
  ['ISTP', 'ISFP', 'ESTP', 'ESFP'],
];

export const TYPE_NAMES: Record<PersonalityType, string> = {
  ENTJ: '指揮官', ENTP: '討論者', ENFJ: '主人公', ENFP: '広報運動家',
  ESTJ: '幹部', ESTP: '起業家', ESFJ: '領事官', ESFP: 'エンターテイナー',
  INTJ: '建築家', INTP: '論理学者', INFJ: '提唱者', INFP: '仲介者',
  ISTJ: '管理者', ISTP: '巨匠', ISFJ: '擁護者', ISFP: '冒険家',
};

export function isEType(type: string | null): boolean {
  return type ? type[0] === 'E' : false;
}

// 4-group color system: NT=purple, NF=green, SJ=blue, SP=orange
export type TypeGroup = 'NT' | 'NF' | 'SJ' | 'SP';

export function getTypeGroup(type: string | null): TypeGroup | null {
  if (!type || type.length < 4) return null;
  const sn = type[1]; // S or N
  const jp = type[3]; // J or P
  if (sn === 'N' && (type[2] === 'T')) return 'NT';
  if (sn === 'N' && (type[2] === 'F')) return 'NF';
  if (sn === 'S' && (jp === 'J')) return 'SJ';
  if (sn === 'S' && (jp === 'P')) return 'SP';
  return null;
}

export const GROUP_COLORS: Record<TypeGroup, string> = {
  NT: '#A78BFA', // purple
  NF: '#4ADE80', // green
  SJ: '#42A5FF', // blue
  SP: '#FF8C42', // orange
};

export const GROUP_LABELS: Record<TypeGroup, string> = {
  NT: '分析家',
  NF: '外交官',
  SJ: '番人',
  SP: '探検家',
};

export function typeGroupColor(type: string | null): string {
  const group = getTypeGroup(type);
  return group ? GROUP_COLORS[group] : '#5C6378';
}

// Legacy E/I binary color (still used in some places)
export function typeColor(type: string | null): string {
  return isEType(type) ? '#FF8C42' : '#42A5FF';
}

export function workStyleLabel(style: string | null): { label: string; colorClass: string } {
  switch (style) {
    case 'remote':
      return { label: 'リモート', colorClass: 'text-primary bg-primary-bg' };
    case 'office':
      return { label: '出社', colorClass: 'text-success bg-success-bg' };
    case 'hybrid':
      return { label: 'ハイブリッド', colorClass: 'text-warning bg-warning-bg' };
    default:
      return { label: '未設定', colorClass: 'text-text-muted bg-surface' };
  }
}

// Color palette
export const C = {
  bg: '#0F1117',
  surface: '#1A1D27',
  surfaceHover: '#222633',
  card: '#1E2130',
  border: '#2A2E3F',
  borderActive: '#4A7CFF',
  primary: '#4A7CFF',
  primaryHover: '#6190FF',
  primaryBg: 'rgba(74,124,255,0.1)',
  accent: '#FF6B6B',
  success: '#4ADE80',
  successBg: 'rgba(74,222,128,0.1)',
  warning: '#FBBF24',
  warningBg: 'rgba(251,191,36,0.1)',
  cautionBg: 'rgba(255,107,107,0.1)',
  text: '#E8ECF4',
  textSub: '#8B92A8',
  textMuted: '#5C6378',
  eType: '#FF8C42',
  iType: '#42A5FF',
} as const;
